import { formatInTimeZone, getTimezoneOffset } from "date-fns-tz"
import { addMinutes, setMinutes, setHours, startOfDay, addMilliseconds, differenceInMilliseconds } from "date-fns"
import _ from "lodash"
import { UK_TIMEZONE } from "./dateTimeHelpers"

// Convert time string to milliseconds in a timezone-aware way
export const convertTimeAsMins = (timeStr, dateStr) => {
  const [hours, minutes] = timeStr.match(/\d+/g).map(Number)
  const date = new Date(dateStr)
  const timeDate = setMinutes(setHours(date, hours), minutes)
  return differenceInMilliseconds(timeDate, startOfDay(date))
}

const convertTimeAsMs = (time) => {
  const [hours, minutes, seconds] = time.match(/\d+/g).map(Number)
  return hours * 3600000 + minutes * 60000 + seconds * 1000
}

const combineSchedules = (schedules) => {
  let combSchedules = _.merge([], schedules)
  combSchedules = combSchedules.map((s) => ({
    ...s,
    schedule_end_time:
      combSchedules.find((s1) => +s1.schedule_start_time === +s.schedule_end_time)?.schedule_end_time ||
      s.schedule_end_time,
  }))
  return combSchedules
}

export const validateBookingSchedule = (
  schedules = [],
  startTime,
  endTime,
  bookings = [],
  chargerSettings = {},
  siteSettings = {}
) => {
  // Validate start time is in future
  if (startTime < new Date()) 
    return {
      isValid: false,
      details: "Booking is in Past"
    }
  // Get timezone-adjusted day of week
  const dayOfWeek = Number(formatInTimeZone(startTime, UK_TIMEZONE, "i")) % 7
  // Get start of day in user's timezone
  let startDate = startOfDay(startTime)

  let bookingDuration = differenceInMilliseconds(endTime, startTime)
  let timeToBooking = differenceInMilliseconds(startTime, new Date())

  if (siteSettings.minimum_booking_notice && timeToBooking < Number(siteSettings.minimum_booking_notice) * 3600000) {
    return {
      isValid: false,
      details: "Too short notice"
    }
  }
  if (chargerSettings.max_booking_period && bookingDuration > convertTimeAsMs(chargerSettings.max_booking_period)) {
    return {
      isValid: false,
      details: "> Max booking period"
    }
  }
  if (chargerSettings.min_booking_period && bookingDuration < convertTimeAsMs(chargerSettings.min_booking_period)) {
    return {
      isValid: false,
      details: "< Min booking period"
    }
  }

  // Convert schedule times to timezone-aware dates
  schedules = schedules.map((s) => {
    const startMs = convertTimeAsMins(s.schedule_start_time, startDate)
    const endMs = convertTimeAsMins(s.schedule_end_time, startDate)

    return {
      ...s,
      schedule_start_time: addMilliseconds(startDate, startMs),
      schedule_end_time: addMilliseconds(startDate, endMs),
    }
  })

  let combSchedules = combineSchedules(schedules)

  // Validate schedule availability
  let valid = !!combSchedules.find(
    (s) =>
      startTime >= s.schedule_start_time &&
      endTime <= s.schedule_end_time &&
      dayOfWeek >= s.schedule_start_day &&
      dayOfWeek <= s.schedule_end_day
  )

  if (!valid) {
    return {
      isValid: false,
      details: !valid ? 'Unavailable' : undefined,
    }
  }
  // Check for booking conflicts
  else {
    valid = !bookings.find((b) => {
      const bookingStart = new Date(b.booking_start_date_time)
      const bookingEnd = new Date(b.booking_end_date_time)

      return (
        (startTime >= bookingStart && startTime < bookingEnd) ||
        (endTime > bookingStart && endTime <= bookingEnd) ||
        (startTime <= bookingStart && endTime >= bookingEnd)
      )
    })

    return {
      isValid: !!valid,
      details: !valid ? 'Already Booked' : undefined,
    }
  }
}

export const getEstimated = (schedules = [], start, end) => {
  const startTime = new Date(start)
  const endTime = new Date(end)
  let currentTime = startTime
  let slots = []

  // Create 30-minute slots in a timezone-aware way
  while (currentTime < endTime) {
    slots.push(+currentTime)
    currentTime = addMinutes(currentTime, 30)
  }

  // Get schedules for the date
  let datedSchs = getDateBookingSchedule(schedules, startTime)

  // Calculate prices for each slot
  slots = slots.map(
    (slot) =>
      datedSchs.find((sch) => slot >= +sch.schedule_start_time && slot <= +sch.schedule_end_time)?.amount_per_kwh
  )

  // Return average price if all slots have prices
  return slots.some((s) => !s) ? false : slots.reduce((p, c) => p + c, 0) / slots.length
}

export const getDateBookingSchedule = (schedules = [], date) => {
  // Get start of day in user's timezone
  const startDate = startOfDay(new Date(date))

  // Get timezone-adjusted day of week
  const dayOfWeek = Number(formatInTimeZone(date, UK_TIMEZONE, "i")) % 7

  let mappedSchedules = schedules.map((s) => ({
    ...s,
    schedule_start_time: addMilliseconds(startDate, convertTimeAsMins(s.schedule_start_time, startDate)),
    schedule_end_time: addMilliseconds(startDate, convertTimeAsMins(s.schedule_end_time, startDate)),
  }))

  mappedSchedules = mappedSchedules.map((s) => {
    if (s.schedule_start_time > s.schedule_end_time) {
      s.schedule_end_time.setDate(s.schedule_end_time.getDate() + 1)
    }
    return s
  })

  return mappedSchedules.filter((s) => dayOfWeek >= s.schedule_start_day && dayOfWeek <= s.schedule_end_day)
}

const breakSchedules = (schedules) => {
  const startDate = startOfDay(new Date())
  let dayWiseSchedules = []

  schedules.forEach((s) => {
    let startDay = s.schedule_start_day
    let endDay = s.schedule_end_day + 1

    while (endDay > startDay) {
      const startMs = convertTimeAsMins(s.schedule_start_time, startDate)
      const endMs = convertTimeAsMins(s.schedule_end_time, startDate)
      let schedule = {
        schedule_day: startDay,
        ...s,
        schedule_start_time: addMilliseconds(startDate, startMs),
        schedule_end_time: addMilliseconds(startDate, endMs),
      }
      // Remove unnecessary properties
      delete schedule.schedule_end_day
      delete schedule.schedule_start_day
      delete schedule.schedule_start_date
      delete schedule.schedule_end_date
      delete schedule.amount_per_kwh
      dayWiseSchedules.push(schedule)
      startDay += 1
    }
  })
  return dayWiseSchedules
}

export const validateSchedule = (schedules) => {
  const dayWiseSchedules = breakSchedules(schedules)

  // Check Overlap
  const isOverlapping = dayWiseSchedules.find((s, i) => {
    return dayWiseSchedules.find(
      (sc, ic) =>
        i !== ic &&
        s.schedule_day === sc.schedule_day &&
        ((s.schedule_start_time >= sc.schedule_start_time && s.schedule_start_time < sc.schedule_end_time) ||
          (s.schedule_end_time > sc.schedule_start_time && s.schedule_end_time <= sc.schedule_end_time) ||
          (s.schedule_start_time <= sc.schedule_start_time && s.schedule_end_time >= sc.schedule_end_time))
    )
  })

  return !isOverlapping
}

export const aggregateSlotsByDay = (slots) => {
  const DAYS = [
    { name: "Monday", dayNumber: 1 },
    { name: "Tuesday", dayNumber: 2 },
    { name: "Wednesday", dayNumber: 3 },
    { name: "Thursday", dayNumber: 4 },
    { name: "Friday", dayNumber: 5 },
    { name: "Saturday", dayNumber: 6 },
    { name: "Sunday", dayNumber: 7 },
  ]
  const groupedSlots = {}
  slots.forEach((slot) => {
    const day = DAYS.find((day) => day.dayNumber === slot.schedule_start_day)?.name
    if (!groupedSlots[day]) groupedSlots[day] = []
    groupedSlots[day].push({
      startTime: slot.schedule_start_time.slice(0, 5),
      endTime: slot.schedule_end_time.slice(0, 5),
      price: slot.amount_per_kwh,
    })
  })
  return Object.entries(groupedSlots)
}